/**
 * The token in an invitation link, or `null` when the URL is not one.
 *
 * An invitation reaches the citizen as an email whose button points at the
 * citizen web's `/accept-invite?token=…`. Read inside the app's Inbox, that
 * link used to leave the app (a browser on the phone, a new tab on the web) —
 * to review an invitation the app already holds. Both Inboxes ask this
 * instead, and open the invitation in place when the token is one of the
 * citizen's own.
 *
 * Any host: the link is built from whichever citizen-web origin the tier uses,
 * and a token only ever opens an invitation the server already lists as the
 * reader's — so the host grants nothing. Absolute or app-relative; the path
 * must be exactly `/accept-invite`. String parsing rather than `URL`, which
 * React Native does not fully implement.
 */
const ACCEPT_INVITE =
  /^(?:[a-z][a-z0-9+.-]*:\/\/[^/?#]+)?\/accept-invite\/?\?(?:[^#]*&)?token=([^&#]+)/i;

export function acceptInviteTokenFrom(url: string): string | null {
  const match = ACCEPT_INVITE.exec(url.trim());
  if (!match) return null;
  try {
    const token = decodeURIComponent(match[1]);
    return token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

/**
 * The sponsorship in a sponsorship-offer link, or `null` when the URL is not
 * one (3 Oct 2026).
 *
 * A company's offer reaches the citizen as an email whose button points at the
 * citizen web's `/sponsorship/offer?token=<sponsorship id>`. An offer is the
 * invitation to be sponsored, so both Inboxes open it in place exactly as they
 * do an invitation link — and, likewise, only when the server lists it as the
 * reader's own, so the host grants nothing. Same rules as
 * `acceptInviteTokenFrom`: any host or app-relative, the path exactly
 * `/sponsorship/offer`.
 */
const SPONSORSHIP_OFFER =
  /^(?:[a-z][a-z0-9+.-]*:\/\/[^/?#]+)?\/sponsorship\/offer\/?\?(?:[^#]*&)?token=([^&#]+)/i;

export function sponsorshipOfferIdFrom(url: string): string | null {
  const match = SPONSORSHIP_OFFER.exec(url.trim());
  if (!match) return null;
  try {
    const id = decodeURIComponent(match[1]);
    return id.length > 0 ? id : null;
  } catch {
    return null;
  }
}
